/**
 * Responsibility: Implement companion modal behavior for the ui/helpers surface.
 * Scope: Owns ui/helpers concerns only; cross-surface orchestration is excluded.
 */
import type { Companion, GameDialogOptions, GameState, ModalAction } from "@anyquest/shared-core"
import { openItemDialog } from '../dialog/dialog-openers'
import { normalizeCompanionSheet } from './sheet-contracts'
import { effectChipText } from './effect-chip'
import { formatVitalValue } from '../../engine/vitals'
function totalMin(gs: GameState): number {
  return (gs.world.clock.day * 24 * 60) + (gs.world.clock.hour * 60) + gs.world.clock.minute
}

function activeCompanionEffects(companion: Companion, nowMin: number): string[] {
  const effects: string[] = []
  for (const mods of Object.values(companion.modifiers ?? {})) {
    for (const mod of mods ?? []) {
      if (mod.expiresAt != null && mod.expiresAt <= nowMin) continue
      effects.push(mod.label || mod.source || 'Effect')
    }
  }
  return [...new Set(effects)]
}

function dormantCompanionEffects(companion: Companion): string[] {
  return [...new Set((companion.dormantEffects ?? []).map(e => e.label || e.source || 'Effect'))]
}

export function buildCompanionDialogOptions(
  gs: GameState | null,
  companion: Companion,
  actions: ModalAction[],
): GameDialogOptions {
  const nowMin = gs ? totalMin(gs) : 0
  const activeFx = activeCompanionEffects(companion, nowMin)
  const dormantFx = dormantCompanionEffects(companion)
  const normalizedSheet = normalizeCompanionSheet({
    identity: companion.identity,
    bio: companion.bio ?? '',
    vitals: companion.vitals,
    abilities: (companion.abilities ?? []).map(ability => ({
      name: ability.name,
      desc: ability.desc,
      cooldown: ability.cooldown,
    })),
    traits: (companion.traits ?? []).map(trait => ({
      name: trait.name,
      desc: trait.desc,
    })),
    inventory: (companion.inventory ?? []).map(item => item.name),
    activeEffects: activeFx,
    dormantEffects: dormantFx,
  })
  return {
    content: 'companion-sheet-dialog',
    data: {
      title: companion.name,
      meta: `${companion.identity.toUpperCase()} · ❤ ${formatVitalValue(companion.vitals, 'vitality')} · ✦ ${formatVitalValue(companion.vitals, 'spirit')}`,
      sheet: normalizedSheet,
      imageUrl: companion.portraitUrl || undefined,
      effectChipText,
      onOpenItem: (itemName: string) => openItemDialog('companion-dialog', itemName, {
        desc: 'Companion item.',
        meta: 'COMPANION ITEM',
      }),
    },
    options: {
      title: companion.name,
      actions,
      variant: 'companion',
      showFooter: true,
    },
  }
}
